import round from 'lodash/round'
import { initialsOf } from '../utils/initials'
import { offsetOf, piecesOf, sourceTimeAt } from '../timing/sceneMap'
import type { Range } from '../timing/skipRanges'
import type { Word } from '../types'

/**
 * Captions follow the media they are bound to (#77).
 *
 * A caption item names a track. Each recording-sourced item on that track
 * contributes the words of its recording that fall inside the PIECES it
 * plays — its window minus the transcript's cuts (`timing/sceneMap.ts`) —
 * rebased onto the output timeline through `offsetOf`, so a word inside a cut
 * (a `skipSegments` entry; removed words are already gone) shows nothing.
 * Words are clamped to the piece and the item's interval, so a trim or a cut
 * landing mid-word never shows a caption over a neighbour or a gap.
 *
 * One pure function, run by the editor's preview and by Lambda from the same
 * document, so dragging a scene moves its captions instantly and the render
 * shows exactly what the preview showed. Words arrive in RECORDING time with
 * `removed` words already dropped (`hydrateTimeline`).
 */

export type CaptionSourceItem = {
  /** Output-timeline start, seconds. */
  from: number
  duration: number
  /** Recording-relative start of the window this item plays. */
  sourceStart: number
  /** Recording-relative end of that window; absent, `sourceStart + duration`. */
  sourceEnd?: number | undefined
  /** The recording's cuts the item plays around; absent, none. */
  skips?: readonly Range[] | undefined
  /** The recording whose words apply, or null for an item without a transcript. */
  recordingId: string | null
}

const EPSILON = 1e-6

/** The kept parts an item plays, recording-relative. */
function piecesOfItem(item: CaptionSourceItem): Range[] {
  const [start, end] = wordsWindow(item)
  const pieces = piecesOf({ start, end }, item.skips ?? [])
  // Nothing kept: the item plays its window from the start (`framePieces`).
  return pieces.length ? pieces : [{ start, end }]
}

export function captionWordsForItems(
  items: readonly CaptionSourceItem[],
  wordsByRecording: Readonly<Record<string, readonly Word[]>>,
): Word[] {
  const out: Word[] = []
  const ordered = [...items].sort((a, b) => a.from - b.from)

  for (const item of ordered) {
    if (!item.recordingId) continue
    const words = wordsByRecording[item.recordingId]
    if (!words?.length) continue

    const pieces = piecesOfItem(item)
    const itemEnd = item.from + item.duration

    for (const word of words) {
      // Overlap, not containment: a word straddling a piece edge is kept and
      // clamped, the same rule `sceneCaptions` applies at a scene edge.
      const first = pieces.find(
        (p) => word.end > p.start + EPSILON && word.start < p.end - EPSILON,
      )
      if (!first) continue
      const last = pieces.findLast(
        (p) => word.end > p.start + EPSILON && word.start < p.end - EPSILON,
      )!
      const startOffset = offsetOf(
        pieces,
        clamp(word.start, first.start, first.end),
      )!
      const endOffset = offsetOf(pieces, clamp(word.end, last.start, last.end))!
      // Milliseconds, like every persisted time: the rebase is a float sum.
      out.push({
        text: word.text,
        start: round(clamp(item.from + startOffset, item.from, itemEnd), 3),
        end: round(clamp(item.from + endOffset, item.from, itemEnd), 3),
      })
    }
  }

  return out
}

/** A speaker span in recording time, as the transcript asset carries it. */
export type SpeakerSpanLike = {
  start: number
  end: number
  name: string | null
  handle: string | null
  /** Absent on a document hydrated before avatars were carried. */
  avatarUrl?: string | null
  /** Absent on a document hydrated before pins existed (#566). */
  participantId?: string | null
}

/** One of a recording's named participants, as the transcript asset lists them (#566). */
export type ParticipantLike = {
  id: string
  name: string | null
  handle: string | null
  avatarUrl: string | null
}

export type CurrentSpeaker = {
  name: string | null
  handle: string | null
  avatarUrl: string | null
  /** Who this is, for a pinned slot to match; null on an older document. */
  participantId: string | null
}

/**
 * Who is speaking at an output time: the item playing then maps the time
 * back into its recording, and the span containing it answers. Through a
 * pause the last speaker holds, and an item that opens in silence shows the
 * first one to speak in it — only spans overlapping the item count, so the
 * editor and the pruned Lambda document agree. `null` for an item nobody
 * speaks in, outside every item, or with no speakers for the recording.
 */
export function speakerAtTime(
  items: readonly CaptionSourceItem[],
  speakersByRecording: Readonly<Record<string, readonly SpeakerSpanLike[]>>,
  time: number,
): CurrentSpeaker | null {
  for (const item of items) {
    if (
      time < item.from - EPSILON ||
      time >= item.from + item.duration - EPSILON
    )
      continue
    if (!item.recordingId) return null
    const spans = speakersByRecording[item.recordingId]
    if (!spans?.length) return null
    const at = sourceTimeAt(piecesOfItem(item), time - item.from)
    const [windowStart, windowEnd] = wordsWindow(item)
    let held: SpeakerSpanLike | null = null
    let next: SpeakerSpanLike | null = null
    for (const s of spans) {
      if (s.end <= windowStart + EPSILON || s.start >= windowEnd - EPSILON)
        continue
      if (at >= s.start - EPSILON && at < s.end - EPSILON) {
        held = s
        next = null
        break
      }
      if (s.start <= at) {
        if (!held || s.start > held.start) held = s
      } else if (!next || s.start < next.start) next = s
    }
    const span = held ?? next
    return span
      ? {
          name: span.name,
          handle: span.handle,
          avatarUrl: span.avatarUrl ?? null,
          participantId: span.participantId ?? null,
        }
      : null
  }
  return null
}

/**
 * The person a pinned slot shows (#566): the directory entry, else any span
 * of theirs (a document hydrated before the directory existed), else null.
 * Never depends on the time: a pinned slot names the same person through
 * the whole scene, whether or not they say anything in it.
 */
export function pinnedSpeaker(
  participants: readonly ParticipantLike[] | undefined,
  spans: readonly SpeakerSpanLike[] | undefined,
  participantId: string,
): CurrentSpeaker | null {
  const entry = participants?.find((p) => p.id === participantId)
  if (entry)
    return {
      name: entry.name,
      handle: entry.handle,
      avatarUrl: entry.avatarUrl,
      participantId,
    }
  const span = spans?.find((s) => s.participantId === participantId)
  return span
    ? {
        name: span.name,
        handle: span.handle,
        avatarUrl: span.avatarUrl ?? null,
        participantId,
      }
    : null
}

/**
 * How long after a participant's last word their slot stays lit (#566): the
 * same tolerance `wordsAndSpeakers` merges adjacent turns with.
 */
export const ACTIVE_HOLD = 1

/**
 * Who is ACTIVE at an output time (#566), the rule a pinned slot lights up
 * by: the participant whose span contains the mapped recording time, or whose
 * span ended within `ACTIVE_HOLD` in the same kept piece with nobody else
 * started since. Unlike `speakerAtTime` it never holds through a silence and
 * never anticipates the next speaker, so when nobody identified is talking
 * (a pause, an unassigned speaker, the seconds before the first word) every
 * pinned slot dims: a lit ring over an unidentified voice would be a false
 * signal. `null` outside every item or with no spans.
 *
 * An unidentified speaker has words but no span, so the recording's words
 * are what end a hold early: any word starting after the held span's last
 * one is someone else's, since a named participant's would have a span of
 * its own that started later.
 */
export function activeSpeakerAt(
  items: readonly CaptionSourceItem[],
  speakersByRecording: Readonly<Record<string, readonly SpeakerSpanLike[]>>,
  time: number,
  wordsByRecording: Readonly<Record<string, readonly { start: number }[]>> = {},
): string | null {
  for (const item of items) {
    if (
      time < item.from - EPSILON ||
      time >= item.from + item.duration - EPSILON
    )
      continue
    if (!item.recordingId) return null
    const spans = speakersByRecording[item.recordingId]
    if (!spans?.length) return null
    const pieces = piecesOfItem(item)
    const at = sourceTimeAt(pieces, time - item.from)
    const piece =
      pieces.find((p) => at >= p.start - EPSILON && at < p.end + EPSILON) ??
      pieces[0]!
    // Containment first (the latest to start, as the label rule picks), so a
    // long turn stays lit through an interjection inside it; else the hold
    // runs from the span that ended last.
    let inside: SpeakerSpanLike | null = null
    let ended: SpeakerSpanLike | null = null
    for (const s of spans) {
      if (!s.participantId) continue
      // Only what this piece plays: a span the cut removed never played.
      if (s.end <= piece.start + EPSILON || s.start >= piece.end - EPSILON)
        continue
      if (s.start > at + EPSILON) continue
      if (at < s.end - EPSILON) {
        if (!inside || s.start > inside.start) inside = s
      } else if (!ended || s.end > ended.end) ended = s
    }
    if (inside) return inside.participantId!
    if (!ended || at - ended.end > ACTIVE_HOLD + EPSILON) return null
    const interrupted = wordsByRecording[item.recordingId]?.some(
      (w) =>
        w.start > ended.end + EPSILON &&
        w.start <= at + EPSILON &&
        w.start < piece.end - EPSILON,
    )
    return interrupted ? null : ended.participantId!
  }
  return null
}

/** A speaker's initials for the avatar placeholder: of the name, else of the handle (`initialsOf`). */
export function speakerInitials(
  speaker: Pick<CurrentSpeaker, 'name' | 'handle'>,
): string {
  return initialsOf(speaker.name ?? speaker.handle?.replace(/^@/, ''))
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value))
}

/**
 * The recording-time window `[start, end]` of words an item can show. Used to
 * prune Lambda `inputProps` to what the bound items reference.
 */
export function wordsWindow(item: CaptionSourceItem): [number, number] {
  return [item.sourceStart, item.sourceEnd ?? item.sourceStart + item.duration]
}

/**
 * Drops every transcript word (or speaker span) no caption-bound item can
 * show. Lambda gets the pruned document; the editor keeps the whole
 * transcript so a trim can reveal words again without a round trip.
 */
export function pruneWords<W extends { start: number; end: number }>(
  words: readonly W[],
  windows: readonly [number, number][],
): W[] {
  if (windows.length === 0) return []
  return words.filter((word) =>
    windows.some(
      ([start, end]) =>
        word.end > start + EPSILON && word.start < end - EPSILON,
    ),
  )
}
